/**
 * Gets the value at `path` of `object`. If the resolved value is
 * `undefined`, the `defaultValue` is returned in its place.
 *
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @param {*} [defaultValue] The value returned for `undefined` resolved values.
 * @returns {*} Returns the resolved value.
 * @example
 *
 * const object = { 'a': [{ 'b': { 'c': 3 } }] };
 *
 * GetTask(object, 'a[0].b.c');
 * // => 3
 *
 * GetTask(object, ['a', '0', 'b', 'c']);
 * // => 3
 *
 * GetTask(object, 'a.b.c', 'default');
 * // => 'default'
 */
export function GetTask(object: any, path: string | string[], defaultValue?: any): any {
    const result = object == null ? undefined : baseGet(object, path)
    return result === undefined ? defaultValue : result
}

/**
 * The base implementation of `get` without support for default values.
 * @private
 */
function baseGet(object: any, path: string | string[]): any {
    const pathArray = castPath(path, object)

    let index = 0
    const length = pathArray.length

    while (object != null && index < length) {
        object = object[toKey(pathArray[index++])]
    }
    return index && index === length ? object : undefined
}

/**
 * Casts `value` to a path array if it's not one.
 * @private
 */
function castPath(value: string | string[], object?: any): string[] {
    if (Array.isArray(value)) {
        return value
    }
    return isKey(value, object) ? [value] : stringToPath(toString(value))
}

/**
 * Checks if `value` is a property name and not a property path.
 * @private
 */
function isKey(value: string, object?: any): boolean {
    if (Array.isArray(value)) {
        return false
    }
    const type = typeof value
    if (
        type === 'number' ||
        type === 'symbol' ||
        type === 'boolean' ||
        value == null ||
        isSymbol(value)
    ) {
        return true
    }
    return (
        reIsPlainProp.test(value) ||
        !reIsDeepProp.test(value) ||
        (object != null && value in Object(object))
    )
}

/**
 * Converts `string` to a property path array.
 * @private
 */
function stringToPath(string: string): string[] {
    const result: string[] = []
    if (string.charCodeAt(0) === 46 /* . */) {
        result.push('')
    }
    string.replace(
        rePropName,
        (match: string, number: string, quote: string, subString: string): string => {
            result.push(quote ? subString.replace(reEscapeChar, '$1') : number || match)
            return ''
        }
    )
    return result
}

/**
 * Converts `value` to a string. An empty string is returned for `null`
 * and `undefined` values. The sign of `-0` is preserved.
 * @private
 */
function toString(value: any): string {
    return value == null ? '' : value + ''
}

/**
 * Converts `value` to a string key if it's not a string or symbol.
 * @private
 */
function toKey(value: any): string {
    if (typeof value === 'string' || isSymbol(value)) {
        return value
    }
    const result = value + ''
    return result === '0' && 1 / value === -INFINITY ? '-0' : result
}

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 * @private
 */
function isSymbol(value: any): boolean {
    return (
        typeof value === 'symbol' ||
        (isObjectLike(value) && objectToString.call(value) === '[object Symbol]')
    )
}

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 * @private
 */
function isObjectLike(value: any): boolean {
    return value != null && typeof value === 'object'
}

// Constants and regex patterns
const INFINITY = 1 / 0
const objectToString = Object.prototype.toString

/** Used to match property names within property paths. */
const rePropName =
    /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*)?\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g

/** Used to match backslashes in property paths. */
const reEscapeChar = /\\(\\)?/g

/** Used to detect if a method is native. */
const reIsNative = /^\[object .+?Constructor\]$/

/** Used to match leading and trailing whitespace. */
const reTrim = /^\s+|\s+$/g

/** Used to detect bad signed hexadecimal string values. */
const reIsBadHex = /^[-+]0x[0-9a-f]+$/i

/** Used to detect binary string values. */
const reIsBinary = /^0b[01]+$/i

/** Used to detect octal string values. */
const reIsOctal = /^0o[0-7]+$/i

/** Used to detect unsigned integer values. */
const reIsUint = /^(?:0|[1-9]\d*)$/

/** Used to match a single whitespace character. */
const reWhitespace = /\s/

/** Used to identify `toStringTag` values of typed arrays. */
const typedArrayTags: { [key: string]: boolean } = {}
typedArrayTags['[object Float32Array]'] =
    typedArrayTags['[object Float64Array]'] =
    typedArrayTags['[object Int8Array]'] =
    typedArrayTags['[object Int16Array]'] =
    typedArrayTags['[object Int32Array]'] =
    typedArrayTags['[object Uint8Array]'] =
    typedArrayTags['[object Uint8ClampedArray]'] =
    typedArrayTags['[object Uint16Array]'] =
    typedArrayTags['[object Uint32Array]'] =
        true
typedArrayTags['[object Arguments]'] =
    typedArrayTags['[object Array]'] =
    typedArrayTags['[object ArrayBuffer]'] =
    typedArrayTags['[object Boolean]'] =
    typedArrayTags['[object DataView]'] =
    typedArrayTags['[object Date]'] =
    typedArrayTags['[object Error]'] =
    typedArrayTags['[object Function]'] =
    typedArrayTags['[object Map]'] =
    typedArrayTags['[object Number]'] =
    typedArrayTags['[object Object]'] =
    typedArrayTags['[object RegExp]'] =
    typedArrayTags['[object Set]'] =
    typedArrayTags['[object String]'] =
    typedArrayTags['[object WeakMap]'] =
        false

/** Used to check objects for own properties. */
const hasOwnProperty = Object.prototype.hasOwnProperty

/** Built-in method references without a dependency on `root`. */
const freeParseInt = parseInt

/** Used to match property names within property paths. */
const reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/
const reIsPlainProp = /^\w*$/

export default GetTask
